import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ShieldCheck, Sparkles, BookOpen, ArrowRight } from "lucide-react";
import { getCategories, getFaqs } from "@/lib/api";
import { SearchBar } from "@/components/SearchBar";
import { CategoryCard } from "@/components/CategoryCard";
import { FaqCard } from "@/components/FaqCard";
import { srcSet } from "@/lib/img";

const HERO = "https://images.unsplash.com/photo-1625047509248-ec889cbff17f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=2000";
const TAGS = [["Brakes squeaking", "squeak"], ["Check engine light", "check engine"], ["P0420", "P0420"], ["AC blowing warm", "warm air"], ["Car won't start", "start"], ["Oil change", "oil"]];

const Hero = () => (
  <section className="relative overflow-hidden grain border-b border-[#27303E]" data-testid="hero-section">
    <img src={HERO} srcSet={srcSet(HERO, [600, 1200, 2000])} sizes="100vw" alt="Mechanic inspecting an engine" fetchPriority="high" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0B0D10] via-[#0B0D10]/85 to-[#0B0D10]/30" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-24 lg:py-36">
      <p className="rise font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-amber-500">// Automotive Repair Answers</p>
      <h1 className="rise font-display text-[2.6rem] sm:text-6xl lg:text-7xl font-black uppercase tracking-tight leading-[0.9] mt-4 sm:mt-5 max-w-3xl" style={{ animationDelay: "80ms" }}>
        Know what's wrong <span className="text-amber-500">before</span> you hit the shop.
      </h1>
      <p className="rise text-[15px] md:text-lg text-slate-300 mt-4 sm:mt-6 max-w-xl" style={{ animationDelay: "160ms" }}>
        Clear, mechanic-grade answers to the most common car repair questions - causes, step-by-step fixes, tools, and real cost ranges.
      </p>
      <div className="rise mt-7 sm:mt-10 max-w-2xl" style={{ animationDelay: "240ms" }}><SearchBar /></div>
      <div className="rise flex sm:flex-wrap gap-2 mt-4 sm:mt-5 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0" style={{ animationDelay: "320ms" }} data-testid="hero-tags">
        {TAGS.map(([t, q]) => (
          <Link key={t} to={`/faqs?q=${encodeURIComponent(q)}`} data-testid={`hero-tag-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="shrink-0 whitespace-nowrap font-mono text-xs px-3 py-2 sm:py-1.5 border border-[#27303E] bg-[#0B0D10]/70 hover:border-amber-500 hover:text-amber-400 active:border-amber-500 rounded-sm transition-colors duration-200">
            {t}
          </Link>
        ))}
      </div>
      <div className="grid grid-cols-3 sm:flex sm:flex-wrap gap-3 sm:gap-8 mt-8 sm:mt-14 font-mono text-[10px] sm:text-xs uppercase tracking-wider sm:tracking-widest text-slate-400">
        <span className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2"><BookOpen size={14} className="text-amber-500" /> 24 detailed repair guides</span>
        <span className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2"><ShieldCheck size={14} className="text-amber-500" /> Safety flags on every answer</span>
        <span className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2"><Sparkles size={14} className="text-amber-500" /> 24/7 AI mechanic</span>
      </div>
    </div>
  </section>
);

const SectionHead = ({ label, title, children }) => (
  <div className="flex flex-row items-end justify-between gap-4 mb-6 sm:mb-10">
    <div>
      <p className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-amber-500">{label}</p>
      <h2 className="font-display text-[1.7rem] sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight leading-none mt-2">{title}</h2>
    </div>
    {children}
  </div>
);

export default function Home() {
  const { data: cats = [] } = useQuery({ queryKey: ["categories"], queryFn: getCategories });
  const { data: popular = [] } = useQuery({ queryKey: ["faqs", "popular"], queryFn: () => getFaqs({ sort: "popular" }) });

  return (
    <main data-testid="home-page">
      <Hero />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-20 lg:py-28">
        <SectionHead label="01 — Systems" title="Browse by category" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="category-grid">
          {cats.map((c, i) => <CategoryCard key={c.slug} cat={c} index={i} />)}
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 sm:pb-10">
        <SectionHead label="02 — Most asked" title="Top repair questions">
          <Link to="/faqs" data-testid="view-all-faqs-link" className="shrink-0 inline-flex items-center gap-1.5 sm:gap-2 font-mono text-[11px] sm:text-xs uppercase tracking-widest text-amber-500 hover:text-amber-300 transition-colors duration-200 py-2">
            View all <span className="hidden sm:inline">questions</span> <ArrowRight size={14} />
          </Link>
        </SectionHead>
        <div className="grid lg:grid-cols-2 gap-3 sm:gap-5" data-testid="popular-faqs">
          {popular.slice(0, 6).map((f, i) => <FaqCard key={f.slug} faq={f} index={i} />)}
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="relative overflow-hidden border border-amber-500/40 bg-[#13171F] rounded-sm p-6 sm:p-10 lg:p-14 flex flex-col lg:flex-row lg:items-center justify-between gap-6 sm:gap-8">
          <div className="hazard absolute top-0 left-0 right-0 h-1.5" />
          <div>
            <h2 className="font-display text-[1.7rem] sm:text-4xl font-extrabold uppercase tracking-tight leading-none">Didn't find your problem?</h2>
            <p className="text-slate-400 text-[15px] sm:text-base mt-3 max-w-xl">Describe the symptom to our AI mechanic and get a tailored diagnosis, repair steps and cost estimate in seconds.</p>
          </div>
          <Link to="/ask" data-testid="cta-ask-ai-btn" className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 active:scale-[0.98] text-[#0B0D10] font-display text-lg font-bold uppercase px-7 h-14 rounded-sm transition-colors duration-200 shrink-0 w-full sm:w-auto">
            <Sparkles size={18} /> Ask the AI Mechanic
          </Link>
        </div>
      </section>
    </main>
  );
}
